import React, { useState, useEffect } from "react";
import { MdVisibility, MdDelete } from "react-icons/md";
import { getImportedFileData } from "../../../Api";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const TabContent = ({
  data,
  reload,
  currentPage,
  itemsPerPage,
  selectedTab,
  onViewFile,
  onDeleteFile,
  selectedFileType,
}) => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const getFileType = (tab, type) => {
    switch (tab) {
      case "UKSanctions":
        return "UK_Sanctions";
      case "NONSDN":
        return "OFAC_NON_SDN";
      case "OFAC":
        return "OFAC_SDN";
      case "Negative":
        return "Others";
      case "PEP":
        return "PEP";
      case "Watch List":
        return "WATCH_LIST";
      case "Address":
        // For Address tab, use the selectedFileType prop
        return selectedFileType || "";
      default:
        return tab;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedTab) {
        console.warn("No tab selected");
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const fileType = getFileType(selectedTab);
        console.log("Fetching files for type:", fileType);

        // For Address tab, don't fetch if no file type is selected
        if (selectedTab === "Address" && !fileType) {
          setTableData([]);
          setError(null);
          return;
        }

        Swal.fire({
          html: `<img src=${require("../../../assets/loader.gif")} style="width: 50px" alt="loader">`,
          showConfirmButton: false,
          heightAuto: false,
          customClass: { popup: "loader-swal" },
          allowOutsideClick: false,
        });

        const response = await getImportedFileData({
          file_type: fileType,
          bank_code: localStorage.getItem("bank_code") || "",
          page: currentPage || 1,
          pageSize: itemsPerPage || 10,
        });

        console.log("Raw API Response:", response);
        Swal.close();

        if (response?.status === "S" && response?.result_info) {
          // Map the API response to our table format
          const files = response.result_info.map((file) => ({
            id: `${file.file_name}_${file.load_date}_${file.load_time}`,
            file_name: file.file_name,
            file_type: fileType, // Use the file type from selectedTab
            created_date: `${file.load_date} ${file.load_time}`,
            bank_code: file.bank_code,
          }));

          console.log("Processed files:", files);
          setTableData(files);
        } else if (
          response.status === "F" &&
          response.message === "Unauthorized - Missing token"
        ) {
          Swal.fire({
            text: "Please login with your credentials",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          }).then(() => {
            localStorage.clear();
            navigate("/login");
          });
        } else if (
          response.status === "F" &&
          response.message === "Unauthorized - Invalid token"
        ) {
          Swal.fire({
            text: "Please login with your credentials",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          }).then(() => {
            localStorage.clear();
            navigate("/login");
          });
        } else {
          console.error("Invalid response format:", response);
          setError(response?.message || "Invalid data format received");
          setTableData([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error?.message || "Failed to fetch files");
        setTableData([]);
      } finally {
        setLoading(false);
      }
    };

    // Use data prop if available, otherwise fetch from API
    if (data && data.length > 0) {
      // Map the data prop to our table format
      const fileType = getFileType(selectedTab);
      const files = data.map((file) => ({
        id: `${file.file_name}_${file.load_date}_${file.load_time}`,
        file_name: file.file_name,
        file_type: fileType,
        created_date: `${file.load_date} ${file.load_time}`,
        bank_code: file.bank_code,
      }));
      setTableData(files);
      setError(null);
    } else {
      // Fetch data from API when data prop is empty or reload is triggered
      fetchData();
    }
  }, [selectedTab, reload, currentPage, itemsPerPage, data, selectedFileType]);

  return (
    <div className="table-responsive">
      <table
        className="table table-bordered table-striped mt-2"
        id="abbreviationsTable"
      >
        <thead className="table-dark">
          <tr>
            <th scope="col">#</th>
            <th scope="col">File Type</th>
            <th scope="col">File Name</th>
            <th scope="col">Created Date</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {!tableData || tableData.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center">
                No files found
              </td>
            </tr>
          ) : (
            tableData.map((file, index) => (
              <tr key={file?.id || index}>
                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                <td>
                  {(file &&
                    (file.file_type === "Others"
                      ? "Miscellaneous"
                      : file.file_type === "WATCH_LIST"
                      ? "WATCH_LIST"
                      : file.file_type)) ||
                    ""}
                </td>
                <td>{file?.file_name || ""}</td>
                {/* <td>
                  {file?.created_date
                    ? new Date(file.created_date).toLocaleString()
                    : ""}
                </td> */}
                <td>
                  {file?.created_date
                    ? (() => {
                        const dateObj = new Date(file.created_date);
                        const day = String(dateObj.getDate()).padStart(2, "0");
                        const month = String(dateObj.getMonth() + 1).padStart(
                          2,
                          "0"
                        ); // Months are 0-indexed
                        const year = dateObj.getFullYear();
                        const time = dateObj.toLocaleTimeString();

                        return `${day}-${month}-${year}, ${time}`;
                      })()
                    : ""}
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => onViewFile && onViewFile(file)}
                      title="View File"
                      aria-label="View file details"
                    >
                      <MdVisibility />
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => onDeleteFile && onDeleteFile(file)}
                      title="Delete File"
                      aria-label="Delete file"
                    >
                      <MdDelete />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TabContent;
